import { hasRequiredExtractInput } from "@mailhand/core/actions";
import type { MessageRow } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Button, Drawer, Field, Select, useToast } from "../../ui/index.js";
import { actionOptionLabel, approvesByDefault, isRunnable } from "./actionChoice.js";
import { type BulkOutcome, bulkSummary, overrideMessages } from "./bulkOverride.js";

interface BulkOverrideDrawerProps {
  open: boolean;
  onClose: () => void;
  rows: readonly MessageRow[];
  onDone: () => void;
}

/** "Change action" for many messages: no typed values and no example, so only actions that need no typed value are listed. */
export function BulkOverrideDrawer({ open, onClose, rows, onDone }: BulkOverrideDrawerProps) {
  const api = useApi();
  const toast = useToast();
  const { actions, reloadApprovals } = useTenant();
  const task = useTask();
  const choices = actions.filter((action) => action.enabled && !hasRequiredExtractInput(action));
  const typedOnly = actions.filter((action) => action.enabled && hasRequiredExtractInput(action));
  const [actionId, setActionId] = useState<string>("");
  const [needsApproval, setNeedsApproval] = useState(false);
  const chosen = choices.find((action) => action.id === actionId);
  const alreadyDone = rows.filter((row) => row.state === "done").length;

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (chosen === undefined) return;
    let results: BulkOutcome[] = [];
    const ok = await task.run(async () => {
      results = await overrideMessages(api, rows.map((row) => row.id), chosen.id, isRunnable(chosen) && needsApproval);
      await reloadApprovals();
    });
    if (!ok) return;
    const anyChanged = results.some((result) => result.outcome === "changed");
    toast.show(bulkSummary(`Changed to "${chosen.name}":`, results), anyChanged ? "success" : "error");
    setActionId("");
    setNeedsApproval(false);
    onDone();
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Change action for ${rows.length === 1 ? "1 message" : `${rows.length} messages`}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="bulk-override-form" busy={task.busy} disabled={chosen === undefined}>
            {chosen === undefined ? "Run action" : isRunnable(chosen) && needsApproval ? `Run "${chosen.name}" after approval` : `Run "${chosen.name}"`}
          </Button>
        </>
      }
    >
      <form id="bulk-override-form" className="stack" onSubmit={(event) => void submit(event)}>
        {alreadyDone === 0 ? null : (
          <div className="note note-warn" role="note">
            {alreadyDone === 1 ? "1 of these messages already ran an action." : `${alreadyDone} of these messages already ran an action.`} The new action runs as well; it does not undo
            the old one.
          </div>
        )}
        <Field label="Action">
          {({ id }) => (
            <Select
              id={id}
              value={actionId}
              onChange={(event) => {
                setActionId(event.target.value);
                setNeedsApproval(approvesByDefault(choices.find((action) => action.id === event.target.value)));
              }}
            >
              <option value="" disabled>
                Pick an action
              </option>
              {choices.map((action) => (
                <option key={action.id} value={action.id}>
                  {actionOptionLabel(action)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {chosen === undefined ? null : <p className="muted small">{chosen.description}</p>}
        {typedOnly.length === 0 ? null : (
          <p className="muted small">
            Not listed, because they need a value typed for each message: {typedOnly.map((action) => action.name).join(", ")}. Change those on one message at a time.
          </p>
        )}
        {isRunnable(chosen) ? (
          <label className="checkbox">
            <input type="checkbox" checked={needsApproval} onChange={(event) => { setNeedsApproval(event.target.checked); }} />
            <span>Wait for my approval before each one runs</span>
          </label>
        ) : null}
      </form>
    </Drawer>
  );
}
